import type { models } from '@org-avp/device-registry';
import { localAveTabId } from 'utils/localAveTabId';

/**
 * Backwards-compatible device type that handles various data formats:
 * - New library format: { type: 'AVE', ... }
 * - Raw API format: { type: 'UHD_WALL', clientClass: 'AVE', ... }
 * - Old mock format: { type: 'AVE', streamUrl: '...', ... }
 */
type DeviceLike = Record<string, unknown>;

/**
 * Type guard to check if a device is an AVE device.
 * Handles both transformed library format and raw API format for resilience.
 *
 * @param device - Device object to check
 * @returns True if device is an AVE type device
 *
 * @example
 * // New library format
 * isAveDevice({ type: 'AVE', id: '123', name: 'Device' }) // true
 *
 * @example
 * // Raw API format (fallback when library transformation fails)
 * isAveDevice({ type: 'UHD_WALL', clientClass: 'AVE', id: '123' }) // true
 *
 * @example
 * // Non-AVE device
 * isAveDevice({ type: 'Chromebox', id: '456' }) // false
 */
export function isAveDevice(device: DeviceLike): device is models.AveDevice {
  // Primary check: transformed library format where type === 'AVE'
  if (device.type === 'AVE') {
    return true;
  }

  // Fallback check: raw API format where clientClass === 'AVE'
  // This handles cases where the device-registry library fails to transform the data
  // The API returns type as screen type (e.g., 'UHD_WALL', 'FHD_WALL') and clientClass as device category
  if ('clientClass' in device && device.clientClass === 'AVE') {
    return true;
  }

  return false;
}

/**
 * Filters an array of devices to only include AVE devices.
 * Backwards-compatible with various data formats.
 *
 * @param devices - Array of device objects
 * @returns Array containing only AVE devices
 */
export function filterAveDevices(devices: DeviceLike[]): models.AveDevice[] {
  return devices.filter(isAveDevice);
}

/**
 * Sorts devices alphabetically by name.
 * Generic function that works with any object having a name property.
 *
 * @param devices - Array of devices to sort
 * @returns New array sorted alphabetically by name
 */
export function sortDevicesByName<T extends { name?: string }>(devices: T[]): T[] {
  return [...devices].sort((a, b) => {
    const nameA = a.name ?? '';
    const nameB = b.name ?? '';
    return nameA.localeCompare(nameB);
  });
}

/**
 * Determines if a device is disabled.
 * A device is considered disabled if it's unavailable AND has no active session.
 *
 * @param device - Device to check
 * @returns True if device is disabled
 */
export function isDeviceDisabled(device: DeviceLike): boolean {
  const available = device.available;
  const sessionId = 'sessionId' in device ? device.sessionId : null;

  return !available && !sessionId;
}

/**
 * Determines whether a device's registry-side session binding belongs to THIS browser tab
 * (i.e. reconnecting is a no-op takeover of the tab's own prior connection, not a foreign one).
 *
 * @param device - Device to check
 * @returns True if the device's sessionId equals this tab's localAveTabId
 */
export function isOwnDeviceBinding(device: DeviceLike): boolean {
  return device.sessionId === localAveTabId;
}

/**
 * Filters devices to only AVE types and sorts them alphabetically by name.
 * Convenience function combining filterAveDevices and sortDevicesByName.
 *
 * @param devices - Array of devices
 * @returns Filtered and sorted array of AVE devices
 */
export function getFilteredSortedAveDevices(devices: DeviceLike[]): models.AveDevice[] {
  const aveDevices = filterAveDevices(devices);
  return sortDevicesByName(aveDevices);
}
